import React, { createContext, ReactNode, useCallback, useContext } from "react";
import { ClientAccessScope, SupervisorTeamClientDetailData } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ErrorState, LoadingState } from "./FeedbackStates";
import { usePortalData } from "../hooks/usePortalData";
import {
  clientWorkspacePath,
  teamClientsPath,
  teamOverviewPath,
} from "../config/navigation";
import { readTeamClinicId, withTeamClinicScope } from "../utils/teamClinicScope";
import { PortalLink } from "./PortalLink";

export interface ClientWorkspaceAccess {
  source: "my-clients" | "team";
  accessScope: ClientAccessScope;
  readOnly: boolean;
  canEditClient: boolean;
  canManageHomework: boolean;
  canEditTreatment: boolean;
  returnPath: string;
  teamContext?: Pick<
    SupervisorTeamClientDetailData,
    "client" | "clinic" | "assignedClinicians" | "access"
  >;
}

const assignedAccess: ClientWorkspaceAccess = {
  source: "my-clients",
  accessScope: "assigned",
  readOnly: false,
  canEditClient: true,
  canManageHomework: true,
  canEditTreatment: true,
  returnPath: "/clinician-portal/clients",
};

const ClientWorkspaceContext = createContext<ClientWorkspaceAccess>(assignedAccess);

export function useClientWorkspace(): ClientWorkspaceAccess {
  return useContext(ClientWorkspaceContext);
}

export function clientWorkspaceBreadcrumbs(
  workspace: ClientWorkspaceAccess,
  patientId: string,
  patientLabel: string,
  currentLabel?: string,
) {
  if (workspace.source !== "team") {
    return [
      { label: "My Clients", path: workspace.returnPath },
      { label: patientLabel },
      ...(currentLabel ? [{ label: currentLabel }] : []),
    ];
  }

  const clinicId = readTeamClinicId();
  return [
    {
      label: "Team",
      path: withTeamClinicScope(teamOverviewPath, clinicId),
    },
    { label: "Clients", path: workspace.returnPath },
    {
      label: patientLabel,
      path: currentLabel
        ? withTeamClinicScope(
            clientWorkspacePath(patientId, "profile", "team"),
            clinicId,
          )
        : undefined,
    },
    ...(currentLabel ? [{ label: currentLabel }] : []),
  ];
}

export function TeamClientWorkspace({
  clinicianCode,
  patientId,
  children,
}: {
  clinicianCode: string;
  patientId: string;
  children: ReactNode;
}) {
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getSupervisorTeamClientDetail(
        clinicianCode,
        patientId,
        signal,
      ),
    [clinicianCode, patientId],
  );
  const state = usePortalData(request);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "supervisor" &&
      data.client &&
      data.access?.scope === "team" &&
      data.access.readOnly,
  );

  if (state.status === "loading") {
    return <LoadingState label="Loading supervised client record" />;
  }
  if (state.status === "error") {
    return <ErrorState error={state.error} onRetry={state.refresh} />;
  }
  if (!data || !available) {
    return <ErrorState error={new Error("Client record is not available")} />;
  }

  const clinicId = readTeamClinicId();
  const returnPath = withTeamClinicScope(teamClientsPath, clinicId);
  const value: ClientWorkspaceAccess = {
    source: "team",
    accessScope: "team",
    readOnly: true,
    canEditClient: false,
    canManageHomework: false,
    canEditTreatment: false,
    returnPath,
    teamContext: {
      client: data.client,
      clinic: data.clinic,
      assignedClinicians: data.assignedClinicians,
      access: data.access,
    },
  };

  return (
    <ClientWorkspaceContext.Provider value={value}>
      {children}
    </ClientWorkspaceContext.Provider>
  );
}

export function ClientWorkspaceContextIndicator({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const workspace = useClientWorkspace();
  if (workspace.source !== "team") return null;
  const names = workspace.teamContext?.assignedClinicians
    .map((clinician) => clinician.therapistName)
    .filter(Boolean);
  return (
    <div className="cp-client-workspace-context" aria-label="Supervised client context">
      <span>Viewing supervised client</span>
      {workspace.teamContext?.clinic?.clinicName ? (
        <span>{workspace.teamContext.clinic.clinicName}</span>
      ) : null}
      {names?.length ? <span>{names.join(", ")}</span> : null}
      <strong>Read-only</strong>
      <PortalLink
        className="cp-client-workspace-context__back"
        path={workspace.returnPath}
        clinicianCode={clinicianCode}
      >
        Back to Team Clients
      </PortalLink>
    </div>
  );
}
